/** What kind of technical failure a raw error message is, or `null` when it is already readable. */
export type FriendlyErrorKind = "generic" | "gemini" | "server" | "unexpected";

// A backend worker's structured `<CODE>: <text>` summary (FIN-1509). The text after a code we
// wrote ourselves is already user copy; the generic codes carry only a class name or a placeholder.
const STRUCTURED_SUMMARY = /^([A-Z][A-Z0-9_]*): (.+)$/s;
const GENERIC_CODE = /^(UNEXPECTED_ERROR|LEGACY_ERROR|REPOSITORY_ERROR|INTERNAL_ERROR|PROVIDER_[A-Z0-9_]*)$/;

/**
 * Classifies a technical/backend error message so a screen can word it in the user's language.
 * `null` means the message is already human-readable and is shown as written.
 */
export function friendlyErrorKind(raw: string): FriendlyErrorKind | null {
  if (!raw) return "generic";

  const structured = STRUCTURED_SUMMARY.exec(raw);
  if (structured) {
    const [, code, text] = structured;
    return GENERIC_CODE.test(code) ? "unexpected" : friendlyText(text);
  }
  return friendlyText(raw);
}

/**
 * The sentence to show when `friendlyErrorKind` found the message readable: our own `<CODE>: <text>`
 * copy without its code, anything else as written.
 */
export function readableErrorText(raw: string): string {
  const structured = STRUCTURED_SUMMARY.exec(raw);
  return structured && !GENERIC_CODE.test(structured[1]) ? structured[2] : raw;
}

function friendlyText(raw: string): FriendlyErrorKind | null {
  // Gemini / LLM API errors
  if (/gemini api error/i.test(raw) || /generativelanguage\.googleapis/i.test(raw)) return "gemini";

  // Generic HTTP-style errors (e.g. "500 - Internal Server Error")
  if (/^\d{3}\s*-/.test(raw)) return "server";

  // JSON blobs leaked as error messages
  if (raw.trim().startsWith("{") || raw.trim().startsWith("[")) return "unexpected";

  // Very long error strings are likely stack traces or serialised objects
  if (raw.length > 200) return "unexpected";

  return null;
}

const ENGLISH: Record<FriendlyErrorKind, string> = {
  generic: "Something went wrong. Please try again.",
  gemini: "Data extraction failed due to a temporary service issue. Please try again.",
  server: "An unexpected server error occurred. Please try again.",
  unexpected: "An unexpected error occurred. Please try again.",
};

/**
 * Converts technical/backend error messages into user-friendly English text, for code that has no
 * language to hand (services). Screens use `useFriendlyError` instead.
 */
export function friendlyError(raw: string): string {
  const kind = friendlyErrorKind(raw);
  return kind ? ENGLISH[kind] : readableErrorText(raw);
}

/**
 * Sanitises a list of warning strings for display.
 */
export function friendlyWarnings(warnings: string[]): string[] {
  return warnings.map(friendlyError);
}
